import React, { useState, useRef, useEffect } from "react";
import { format, parse } from "date-fns";
import { ChevronLeft, ChevronRight, Calendar, Clock, X, Move } from "lucide-react";
import { Button } from "./button";

const daysShort = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const formatDateToISO = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getLocaleDateFormat = () => {
  const locale = navigator.language || navigator.userLanguage || 'en-US';
  const ddMMCountries = ['IN', 'GB', 'AU', 'NZ', 'ZA', 'FR', 'DE', 'IT', 'ES', 'BR', 'PT', 'NL', 'BE', 'GR', 'IE'];
  const countryCode = locale.split('-')[1] || locale.split('_')[1] || '';
  if (ddMMCountries.includes(countryCode)) {
    return 'dd/MM/yyyy';
  }
  return 'MM/dd/yyyy';
};

const parseLocaleDate = (dateString, formatPattern) => {
  let parsed = parse(dateString, formatPattern, new Date());

  if (!isNaN(parsed) && parsed.getFullYear() > 1900 && parsed.getFullYear() < 2100) {
    return parsed;
  }

  const altFormat = formatPattern === 'MM/dd/yyyy' ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
  parsed = parse(dateString, altFormat, new Date());

  if (!isNaN(parsed) && parsed.getFullYear() > 1900 && parsed.getFullYear() < 2100) {
    return parsed;
  }

  const isoParsed = new Date(dateString);
  if (!isNaN(isoParsed) && isoParsed.getFullYear() > 1900 && isoParsed.getFullYear() < 2100) {
    return isoParsed;
  }

  return new Date(NaN);
};

export default function DatePicker({
  value,
  onChange,
  placeholder = "Select date",
  className = "",
  disabled = false,
}) {
  const [showPopup, setShowPopup] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [inputValue, setInputValue] = useState("");
  const [dateFormat] = useState(() => getLocaleDateFormat());
  const popupRef = useRef(null);
  const dragData = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0, dragging: false });

  useEffect(() => {
    if (value) {
      // Parse YYYY-MM-DD as local time
      let date;
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m, d] = value.split('-').map(Number);
        date = new Date(y, m - 1, d);
      } else {
        date = new Date(value);
      }

      if (!isNaN(date.getTime())) {
        setCurrentMonth(date.getMonth());
        setCurrentYear(date.getFullYear());
        setInputValue(format(date, dateFormat));
      }
    }
  }, [value, dateFormat]);

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDay = new Date(currentYear, currentMonth, 1).getDay();
  const prevMonthDays = new Date(currentYear, currentMonth, 0).getDate();

  const calendar = [];
  let dayCounter = 1 - firstDay;

  for (let i = 0; i < 6; i++) {
    const week = [];
    for (let j = 0; j < 7; j++) {
      let type = "current";
      let day = dayCounter;

      if (dayCounter < 1) {
        day = prevMonthDays + dayCounter;
        type = "prev";
      } else if (dayCounter > daysInMonth) {
        day = dayCounter - daysInMonth;
        type = "next";
      }

      week.push({ day, type });
      dayCounter++;
    }
    calendar.push(week);
  }

  const handlePrevMonth = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else setCurrentMonth((m) => m - 1);
  };

  const handleNextMonth = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else setCurrentMonth((m) => m + 1);
  };

  const handleDateClick = (day, type) => {
    let year = currentYear;
    let month = currentMonth;
    if (type === "prev") {
      month = currentMonth === 0 ? 11 : currentMonth - 1;
      if (currentMonth === 0) year -= 1;
    } else if (type === "next") {
      month = currentMonth === 11 ? 0 : currentMonth + 1;
      if (currentMonth === 11) year += 1;
    }

    const selectedDate = new Date(year, month, day);
    onChange(formatDateToISO(selectedDate));
    setInputValue(format(selectedDate, dateFormat));
    setShowPopup(false);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    const parsedDate = parseLocaleDate(val, dateFormat);
    if (!isNaN(parsedDate)) {
      onChange(formatDateToISO(parsedDate));
      setCurrentMonth(parsedDate.getMonth());
      setCurrentYear(parsedDate.getFullYear());
    }
  };

  const handleTodayClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const today = new Date();
    onChange(formatDateToISO(today));
    setInputValue(format(today, dateFormat));
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
    setShowPopup(false);
  };

  // Outside click close
  useEffect(() => {
    if (!showPopup) return;
    
    function handleClickOutside(e) {
      if (!popupRef.current) return;
      
      // Check if click is inside the popup
      const target = e.target;
      const popup = popupRef.current;
      
      // Check if the target is the popup or any of its children
      if (popup.contains(target)) {
        return; // Click is inside popup, don't close
      }
      
      // Check if clicking on a button or its children (SVG icons)
      const clickedButton = target.closest('button');
      if (clickedButton && popup.contains(clickedButton)) {
        return; // Click is on a button inside popup, don't close
      }
      
      // Click is outside, close the popup
      setShowPopup(false);
    }
    
    // Use a small delay to ensure button clicks are processed first
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopup]);

  // 🟢 Drag handling
  const handleMouseDown = (e) => {
    const popup = popupRef.current;
    if (!popup) return;
    dragData.current.dragging = true;
    dragData.current.offsetX = e.clientX - popup.offsetLeft;
    dragData.current.offsetY = e.clientY - popup.offsetTop;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!dragData.current.dragging) return;
    const popup = popupRef.current;
    popup.style.left = `${e.clientX - dragData.current.offsetX}px`;
    popup.style.top = `${e.clientY - dragData.current.offsetY}px`;
  };

  const handleMouseUp = () => {
    dragData.current.dragging = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  return (
    <>
      {/* Input field */}
      <div className={`relative ${className}`}>
        <div
          className={`border rounded-lg px-3 py-2 flex justify-between items-center bg-white ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-gray-400"
            }`}
        >
          <div className="flex items-center gap-2 w-full">
            <Calendar className="h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={placeholder}
              value={inputValue}
              onChange={handleInputChange}
              onFocus={() => !disabled && setShowPopup(true)}
              className="outline-none w-full text-gray-900 placeholder-gray-500 cursor-pointer"
            />
          </div>
        </div>
      </div>

      {/* Floating draggable popup */}
      {showPopup && !disabled && (
        <div className="fixed inset-0 bg-black/30 z-[9998] flex items-center justify-center">
          <div
            ref={popupRef}
            className="absolute bg-white rounded-xl shadow-2xl p-4 w-[min(320px,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] cursor-default"
            style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}
          >
            {/* Header (draggable area) */}
            <div
              className="flex items-center justify-between cursor-move pb-2 border-b mb-3"
              onMouseDown={handleMouseDown}
            >
              <div className="flex items-center gap-2 text-gray-700 font-medium">
                <Move className="h-4 w-4 text-gray-500" />
                Drag Calendar
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPopup(false);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>

            {/* Today Button */}
            <div className="mb-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleTodayClick}
                className="w-full"
              >
                <Clock className="h-4 w-4 mr-2" />
                Today
              </Button>
            </div>

            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                className="p-1 rounded hover:bg-gray-100"
                onClick={handlePrevMonth}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="font-semibold text-gray-900 text-sm">
                {format(new Date(currentYear, currentMonth), "MMMM yyyy")}
              </span>
              <button
                type="button"
                className="p-1 rounded hover:bg-gray-100"
                onClick={handleNextMonth}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Week header */}
            <div className="grid grid-cols-7 mb-1 text-xs font-medium text-gray-500 text-center">
              {daysShort.map((d) => (
                <div key={d}>{d}</div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 text-xs text-center gap-1">
              {calendar.flat().map(({ day, type }, idx) => {
                let valueDate = null;
                if (value) {
                  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
                    const [y, m, d] = value.split('-').map(Number);
                    valueDate = new Date(y, m - 1, d);
                  } else {
                    valueDate = new Date(value);
                  }
                }

                const isSelected =
                  valueDate &&
                  valueDate.getDate() === day &&
                  valueDate.getMonth() === currentMonth &&
                  valueDate.getFullYear() === currentYear &&
                  type === "current";

                const isFaded = type !== "current";

                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDateClick(day, type);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className={`aspect-square w-full flex items-center justify-center rounded-md transition-colors ${isSelected
                        ? "bg-blue-600 text-white font-semibold"
                        : isFaded
                          ? "text-gray-300"
                          : "hover:bg-gray-100 text-gray-800"
                      }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
